import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Plus, User, X } from 'lucide-react';
import { useLanguage } from '../../StoreContext/LanguageContext';
import apiService from '../../config'; 
import { useRef } from "react";

const HighwayEditLandholdingList = ({ onBack, onSuccess, LandAcquisition_id }) => {
    const { language, translations } = useLanguage();
    const [formData, setFormData] = useState({
        project_id: '',
        village_name: '',
        dist_name: '',
        taluka_name: '',
        landholder_name: '',
        survey_no: '',
        survey_type: 'Survey No.',
        irrigable_type: '',
        irrigable_area: 0,
        non_irrigable_area: 0,
        total_area: 0,
        north: "",
        east: "",
        west: "",
        south: "",
        area_unit_id: "",
        aqr_area: 0,
        unit_name: 'ACRE',
        is_active: 1,
        land_holding_type: ""
    });
    const [loading, setLoading] = useState(true);
    const [areaUnits, setAreaUnits] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [currentLandId, setCurrentLandId] = useState(null);
    const [districts, setDistricts] = useState([]);
    const [talukas, setTalukas] = useState([]);
    const [villages, setVillages] = useState([]);
    const [editDistrict, setEditDistrict] = useState(false);
    const [editTaluka, setEditTaluka] = useState(false);
    const [editVillage, setEditVillage] = useState(false);
    const [selectedDistrictId, setSelectedDistrictId] = useState(null);
    const [selectedTalukaId, setSelectedTalukaId] = useState(null);
    const [errors, setErrors] = useState({});

    // Owner/Occupant related states
    const [existingOwners, setExistingOwners] = useState([]);
    const [showAddOwnerForm, setShowAddOwnerForm] = useState(false);
    const [addingOwner, setAddingOwner] = useState(false);
    const [newOwnerData, setNewOwnerData] = useState({
        owner_name: '',
        survey_no: '',
        owner_total_area: '',
        owner_aqr_area: '',
        owner_irrigable_area: "",
        owner_non_irrigable_area: ""
    });

    // Total Area tracking states
    const [totalCompletedOwnerArea, setTotalCompletedOwnerArea] = useState(0);
    const [remainingTotalArea, setRemainingTotalArea] = useState(0);
    const [isTotalAreaComplete, setIsTotalAreaComplete] = useState(false);

    // NEW: Acquired Area tracking states
    const [totalCompletedAcquiredArea, setTotalCompletedAcquiredArea] = useState(0);
    const [remainingAcquiredArea, setRemainingAcquiredArea] = useState(0);
    const [isAcquiredAreaComplete, setIsAcquiredAreaComplete] = useState(false);

    // Alert and confirm modal states
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertConfig, setAlertConfig] = useState({
        title: '',
        message: '',
        type: 'success'
    });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        title: '',
        message: '',
        onConfirm: null,
        onCancel: null,
        type: 'warning'
    });

    const [showEditOwnerForm, setShowEditOwnerForm] = useState(false);
    const [editingOwner, setEditingOwner] = useState(false);
    const [editOwnerData, setEditOwnerData] = useState({
        land_owner_id: '',
        owner_name: '',
        survey_no: '',
        owner_total_area: '',
        owner_aqr_area: '',
        owner_irrigable_area: '',
        owner_non_irrigable_area: ''
    });
    const [currentEditingOwnerId, setCurrentEditingOwnerId] = useState(null);
    const [originalOwnerData, setOriginalOwnerData] = useState(null);
    const [landHoldingTypes, setLandHoldingTypes] = useState([]);

    const addOwnerFormRef = useRef(null);
    const editOwnerFormRef = useRef(null);

    // Helper functions for modals
    const showAlert = (title, message, type = 'info') => {
        setAlertConfig({ title, message, type });
        setShowAlertModal(true);

        // Don't auto-hide success messages so user can see them
        if (type !== 'success') {
            setTimeout(() => setShowAlertModal(false), 5000);
        }
    };

    const showConfirm = (title, message, onConfirm, onCancel = null, type = 'warning') => {
        setConfirmConfig({ title, message, onConfirm, onCancel, type });
        setShowConfirmModal(true);
    };

    // Calculate total area completion status
    const calculateTotalAreaCompletion = (owners = existingOwners) => {
        const totalCompleted = owners.reduce((sum, owner) => {
            return sum + (parseFloat(owner.owner_total_area) || 0);
        }, 0);

        const landTotalArea = parseFloat(formData.total_area) || 0;
        const remaining = Math.max(0, landTotalArea - totalCompleted);
        const isComplete = totalCompleted >= landTotalArea;

        setTotalCompletedOwnerArea(totalCompleted);
        setRemainingTotalArea(remaining);
        setIsTotalAreaComplete(isComplete);

        return { totalCompleted, remaining, isComplete };
    };

    // NEW: Calculate acquired area completion status
    const calculateAcquiredAreaCompletion = (owners = existingOwners) => {
        const acquiredCompleted = owners.reduce((sum, owner) => {
            return sum + (parseFloat(owner.owner_aqr_area) || 0);
        }, 0);

        const landAcquiredArea = parseFloat(formData.aqr_area) || 0;
        const remainingAcquired = Math.max(0, landAcquiredArea - acquiredCompleted);
        const isAcquiredComplete = acquiredCompleted >= landAcquiredArea;

        setTotalCompletedAcquiredArea(acquiredCompleted);
        setRemainingAcquiredArea(remainingAcquired);
        setIsAcquiredAreaComplete(isAcquiredComplete);

        return { acquiredCompleted, remainingAcquired, isAcquiredComplete };
    };

    // Update area calculations when owners or form data changes
    useEffect(() => {
        calculateTotalAreaCompletion();
        calculateAcquiredAreaCompletion(); // NEW: Also calculate acquired area
    }, [existingOwners, formData.total_area, formData.aqr_area]);

    // Reset new owner form with pre-filled data
    const resetNewOwnerForm = () => {
        setNewOwnerData({
            owner_name: '',
            survey_no: formData.survey_no, // Pre-filled from landholding
            owner_total_area: remainingTotalArea > 0 ? remainingTotalArea : '', // Remaining total area
            owner_aqr_area: remainingAcquiredArea > 0 ? remainingAcquiredArea : '', // NEW: Remaining acquired area
            owner_irrigable_area: '', // NEW
            owner_non_irrigable_area: ''
        });
    };

    // ---------------------------Edit Occupant--------------------------- 
    const calculateEditingAreaLimits = (originalOwnerData) => {
        // Add back the original owner's areas to get true remaining amounts
        const availableTotalArea = remainingTotalArea + (parseFloat(originalOwnerData?.owner_total_area) || 0);
        const availableAcquiredArea = remainingAcquiredArea + (parseFloat(originalOwnerData?.owner_aqr_area) || 0);

        return {
            availableTotalArea,
            availableAcquiredArea
        };
    };

    // Reset edit owner form with data from selected owner
    const resetEditOwnerForm = (ownerData = null) => {
        if (ownerData) {
            setEditOwnerData({
                land_owner_id: ownerData.land_owner_id || '',
                owner_name: ownerData.owner_name || '',
                survey_no: ownerData.survey_no || formData.survey_no,
                owner_total_area: ownerData.owner_total_area || '',
                owner_aqr_area: ownerData.owner_aqr_area || '',
                owner_irrigable_area: ownerData.owner_irrigable_area || '',
                owner_non_irrigable_area: ownerData.owner_non_irrigable_area || ''
            });
            setOriginalOwnerData(ownerData);
            setCurrentEditingOwnerId(ownerData.land_owner_id);
        } else {
            setEditOwnerData({
                land_owner_id: '',
                owner_name: '',
                survey_no: formData.survey_no,
                owner_total_area: '',
                owner_aqr_area: '',
                owner_irrigable_area: '',
                owner_non_irrigable_area: ''
            });
            setOriginalOwnerData(null);
            setCurrentEditingOwnerId(null);
        }
    };

    //Handle showing edit owner form with pre-filled data
    const handleShowEditOwnerForm = (owner) => {
        // Close add form if it's open (NEW CODE)
        if (showAddOwnerForm) {
            setShowAddOwnerForm(false);
            resetNewOwnerForm();
        }

        // Existing code for opening edit form
        resetEditOwnerForm(owner);
        setShowEditOwnerForm(true);

        setTimeout(() => {
            if (editOwnerFormRef.current) {
                editOwnerFormRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }
        })
    };

    // helper for safe 2-decimal rounding (use only when finalizing)
const safeTwoDecimals = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return "";
  return parseFloat(num.toFixed(4));
};

    // Handle edit owner form data changes with validation
const handleEditOwnerChange = (e) => {
  const { name, value } = e.target;
  let newValue = value;

  // --- Total & Acquired area ---
  if (['owner_total_area', 'owner_aqr_area'].includes(name)) {
    if (value === "") {
      newValue = "";
    } else if (/^\d*\.?\d{0,4}$/.test(value)) {
      // let the user type "1.", "1.2", etc.
      newValue = value;
    } else {
      // too many decimals → round safely
      newValue = safeTwoDecimals(value).toString();
    }
  }

  // --- Irrigable & Non-Irrigable ---
  if (['owner_irrigable_area', 'owner_non_irrigable_area'].includes(name)) {
    if (value === "") {
      newValue = "";
    } else if (/^\d*\.?\d{0,4}$/.test(value)) {
      newValue = value;
    } else {
      newValue = safeTwoDecimals(value).toString();
    }

    const acquired = parseFloat(editOwnerData.owner_aqr_area) || 0; // 🔹 use acquired, not total
    const irrig = name === 'owner_irrigable_area'
      ? parseFloat(newValue) || 0
      : parseFloat(editOwnerData.owner_irrigable_area) || 0;
    const nonIrrig = name === 'owner_non_irrigable_area'
      ? parseFloat(newValue) || 0
      : parseFloat(editOwnerData.owner_non_irrigable_area) || 0;

    if (irrig + nonIrrig > acquired) {
      const maxAllowed = acquired - (name === 'owner_irrigable_area'
        ? (parseFloat(editOwnerData.owner_non_irrigable_area) || 0)
        : (parseFloat(editOwnerData.owner_irrigable_area) || 0));

      newValue = safeTwoDecimals(maxAllowed).toString();

      showAlert(
        'Validation',
        `${translations[language][name === 'owner_irrigable_area'
          ? 'IrrigableArea'
          : 'NonIrrigableArea']} cannot exceed ${maxAllowed.toFixed(4)} ${formData.unit_name}`,
        'warning'
      );
    }
  }

  // --- Clamp total & acquired area against limits ---
  const { availableTotalArea, availableAcquiredArea } = calculateEditingAreaLimits(originalOwnerData);

  if (name === 'owner_total_area' && value !== "") {
    const enteredArea = parseFloat(value);
    if (enteredArea > availableTotalArea) {
      showAlert(
        'Area Limit Exceeded',
        `Maximum available total area is ${availableTotalArea.toFixed(4)} ${formData.unit_name}`,
        'warning'
      );
      newValue = availableTotalArea.toString();
    }
  }

  if (name === 'owner_aqr_area' && value !== "") {
    const enteredAcquiredArea = parseFloat(value);
    if (enteredAcquiredArea > availableAcquiredArea) {
      showAlert(
        'Acquired Area Limit Exceeded',
        `Maximum available acquired area is ${availableAcquiredArea.toFixed(4)} ${formData.unit_name}`,
        'warning'
      );
      newValue = availableAcquiredArea.toString();
    }
  }

  setEditOwnerData(prev => ({
    ...prev,
    [name]: newValue
  }));
};




    //Handle edit owner/occupant submission
    const handleEditOwner = async (e) => {
        e.preventDefault();

        // Validation checks
        if (!currentLandId || !formData.project_id || !formData.proposal_id || !currentEditingOwnerId) {
            showAlert('Error', 'Missing required data to edit owner', 'error');
            return;
        }

        // Get available area limits
        const { availableTotalArea, availableAcquiredArea } = calculateEditingAreaLimits(originalOwnerData);

        // Validate total area
        const enteredTotalArea = parseFloat(editOwnerData.owner_total_area) || 0;
        if (enteredTotalArea > availableTotalArea) {
            showAlert(
                'Invalid Area',
                `Total area cannot exceed available area of ${availableTotalArea.toFixed(4)} ${formData.unit_name}`,
                'error'
            );
            return;
        }

        if (enteredTotalArea <= 0) {
            showAlert('Invalid Area', 'Total area must be greater than 0', 'error');
            return;
        }

        // Validate acquired area
        const enteredAcquiredArea = parseFloat(editOwnerData.owner_aqr_area) || 0;
        if (enteredAcquiredArea > availableAcquiredArea) {
            showAlert(
                'Invalid Acquired Area',
                `Acquired area cannot exceed available acquired area of ${availableAcquiredArea.toFixed(4)} ${formData.unit_name}`,
                'error'
            );
            return;
        }

        // Validate that acquired area doesn't exceed owner's total area
        if (enteredAcquiredArea > enteredTotalArea) {
            showAlert('Invalid Area', 'Acquired area cannot exceed owner\'s total area', 'error');
            return;
        }

        const irrig = parseFloat(editOwnerData.owner_irrigable_area) || 0;
        const nonIrrig = parseFloat(editOwnerData.owner_non_irrigable_area) || 0;

        if (irrig + nonIrrig > enteredAcquiredArea) {
            showAlert('Invalid Area',
                `Irrigable + Non-Irrigable area cannot exceed ${enteredAcquiredArea} ${formData.unit_name}`,
                'error'
            );
            return;
        }

        try {
            setEditingOwner(true);

            // Prepare payload for update
            const payload = {
                project_id: formData.project_id,
                proposal_id: formData.proposal_id,
                LandAcquisition_id: currentLandId,
                owner_name: editOwnerData.owner_name,
                survey_no: formData.survey_no,
                dist_name: formData.district_id,
                taluka_name: formData.taluka_id,
                village_name: formData.village_id,
                ...(editOwnerData.owner_total_area ? { owner_total_area: parseFloat(editOwnerData.owner_total_area) } : {}),
                ...(editOwnerData.owner_aqr_area ? { owner_aqr_area: parseFloat(editOwnerData.owner_aqr_area) } : {}),
                ...(editOwnerData.owner_irrigable_area ? { owner_irrigable_area: parseFloat(editOwnerData.owner_irrigable_area) } : {}),
                ...(editOwnerData.owner_non_irrigable_area ? { owner_non_irrigable_area: parseFloat(editOwnerData.owner_non_irrigable_area) } : {})
            };

            console.log('Updating owner with ID:', currentEditingOwnerId, 'payload:', payload);

            // Make API call to update owner (you'll need to implement this in your apiService)
            const result = await apiService.patchUpdateHighwayMeasurement(currentEditingOwnerId, payload);

            if (result && (result.ok || result.status === 200)) {
                showAlert('Success', 'Owner updated successfully!', 'success');

                // Reset form and close modal
                resetEditOwnerForm();
                setShowEditOwnerForm(false);

                // Refresh the landholding data to get updated owners list
                setTimeout(() => {
                    fetchLandholdingData(currentLandId);
                }, 1000);
            } else {
                throw new Error('Failed to update owner');
            }

        } catch (error) {
            console.error('Error updating owner:', error);
            showAlert('Error', `Failed to update owner: ${error.message}`, 'error');
        } finally {
            setEditingOwner(false);
        }
    };

    const closeBothForms = () => {
        if (showAddOwnerForm) {
            setShowAddOwnerForm(false);
            resetNewOwnerForm();
        }
        if (showEditOwnerForm) {
            setShowEditOwnerForm(false);
            resetEditOwnerForm();
        }
    };

    // Individual close handlers for cleaner code (OPTIONAL)
    const closeAddOwnerForm = () => {
        setShowAddOwnerForm(false);
        resetNewOwnerForm();
    };

    const closeEditOwnerForm = () => {
        setShowEditOwnerForm(false);
        resetEditOwnerForm();
    };

    //load fetch area units
    useEffect(() => {
        const fetchAreaUnits = async () => {
            try {
                const res = await apiService.getAreaUnits();
                setAreaUnits(res.data || []);
            } catch (err) {
                console.error("Failed to load area units:", err.message);
            }
        };

        fetchAreaUnits();
    }, []);

    // fetch landholding types
    useEffect(() => {
        const fetchLandHoldingTypes = async () => {
            try {
                const res = await apiService.getLandHoldingType();
                const types = res.data || [];
                setLandHoldingTypes(types);

                // Optional: auto-select first type if none is selected
                if (types.length > 0 && !formData.land_holding_type) {
                    // setFormData((prev) => ({
                    //   ...prev,
                    //   land_holding_type: types[0].land_holding_type, // Store name
                    // }));
                }
            } catch (err) {
                console.error("Failed to load land holding types:", err.message);
            }
        };

        fetchLandHoldingTypes();
    }, []);

    // Fetch districts on component mount
    useEffect(() => {
        const fetchDistricts = async () => {
            try {
                const response = await apiService.getDistricts();
                // Handle both .data and .record response formats
                const districtsList = response.data || response.record || [];
                setDistricts(districtsList);
                console.log('Districts loaded:', districtsList);
            } catch (error) {
                console.error("Failed to load districts:", error);
            }
        };
        fetchDistricts();
    }, []);

    // 2. FIXED: Fetch talukas when district changes
    useEffect(() => {
        const fetchTalukas = async () => {
            setTalukas([]);
            setVillages([]);

            if (formData.district_id) {
                try {
                    console.log('Fetching talukas for district:', formData.district_id);
                    const response = await apiService.getTalukas(formData.district_id);
                    // Handle both .data and .record response formats
                    const talukasList = response.data || response.record || [];
                    setTalukas(talukasList);
                } catch (error) {
                    console.error("Failed to load talukas:", error);
                    setTalukas([]);
                }
            }
        };

        fetchTalukas();
    }, [formData.district_id]);

    // 3. FIXED: Fetch villages when district and taluka change
    useEffect(() => {
        const fetchVillages = async () => {
            setVillages([]);

            if (formData.district_id && formData.taluka_id) {
                try {
                    console.log('Fetching villages for district:', formData.district_id, 'taluka:', formData.taluka_id);
                    const response = await apiService.getVillages(formData.district_id, formData.taluka_id);
                    // Handle both .data and .record response formats
                    const villagesList = response.data || response.record || [];
                    setVillages(villagesList);
                } catch (error) {
                    console.error("Failed to load villages:", error);
                    setVillages([]);
                }
            }
        };

        fetchVillages();
    }, [formData.district_id, formData.taluka_id]);

    // Load landholding data when component mounts
    useEffect(() => {
        initializeComponent();
    }, [LandAcquisition_id]);

    const initializeComponent = async () => {
        try {
            setLoading(true);
            setError(null);

            let landIdToUse = null;
            let dataToLoad = null;

            // Priority 1: Use prop landId if provided
            if (LandAcquisition_id && LandAcquisition_id !== 'undefined' && LandAcquisition_id !== undefined) {
                landIdToUse = LandAcquisition_id;
                console.log('Using landId from props:', landIdToUse);
            } else {
                // Priority 2: Try to get data from localStorage
                const editingData = localStorage.getItem('editingLandholding');
                if (editingData) {
                    try {
                        dataToLoad = JSON.parse(editingData);
                        // The landId is stored in groupNo field after transformation
                        landIdToUse = dataToLoad.groupNo || dataToLoad.LandAcquisition_id;

                        console.log('Data from localStorage:', dataToLoad);
                        console.log('Extracted landId:', landIdToUse);
                    } catch (parseError) {
                        console.error('Error parsing localStorage data:', parseError);
                        setError('Failed to load landholding data from storage.');
                        setLoading(false);
                        return;
                    }
                }
            }

            // Validate landId
            if (!landIdToUse || landIdToUse === 'undefined' || landIdToUse === undefined) {
                setError('No valid land ID found. Cannot load landholding data.');
                setLoading(false);
                return;
            }

            setCurrentLandId(landIdToUse);

            // Try to fetch fresh data from API first
            try {
                await fetchLandholdingData(landIdToUse);
            } catch (fetchError) {
                console.warn('Failed to fetch from API, using localStorage data:', fetchError);

                // Fallback to localStorage data if API fails
                if (dataToLoad) {
                    mapDataToFormState(dataToLoad);
                    // setError('Loaded from cache - some data may not be current.');
                } else {
                    throw fetchError;
                }
            }

        } catch (error) {
            console.error('Error initializing component:', error);
            setError('Failed to initialize edit form. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const mapDataToFormState = async (data) => {
        console.log('Mapping data to form state:', data);

        const record = Array.isArray(data) ? data[0] : data;

        // Map the data to form state with correct field names
        const mappedFormData = {
            project_id: record.project_id || '',
            proposal_id: record.proposal_id,
            district_id: record.dist_name || record.district_id || '', // Use dist_name consistently
            taluka_id: record.taluka_name || record.taluka_id || '',   // Use taluka_name consistently
            village_id: record.village_name || record.village_id || '', // Use village_name consistently
            LandAcquisition_id: record.groupNo || record.LandAcquisition_id || '',
            landholder_name: record.landholdersName || record.landholder_name || '',
            survey_no: record.SurveyNO || record.survey_no || '',
            survey_type: record.survey_type || 'Survey No.',
            irrigable_type: record.irrigable_type || 'Canal',
            irrigable_area: parseFloat(record.irrigableArea || record.irrigable_area) || 0,
            non_irrigable_area: parseFloat(record.nonIrrigableArea || record.non_irrigable_area) || 0,
            total_area: parseFloat(record.totalArea || record.total_area) || 0,
            aqr_area: parseFloat(record.AquiredArea || record.aqr_area) || 0,
            unit_name: record.unit_name || 'ACRE',
            north: record.north || '',
            east: record.east || '',
            west: record.west || '',
            south: record.south || '',
            area_unit_id: record.area_unit_id || '',
            is_active: record.status !== undefined ? (record.status ? 1 : 0) : (record.is_active !== undefined ? record.is_active : 1),
            land_holding_type: record.land_holding_type || '',
        };

        setFormData(mappedFormData);
        console.log('Form data set:', mappedFormData);

        // Set existing owners if available
        if (record.owners && Array.isArray(record.owners)) {
            setExistingOwners(record.owners);
        }

        // Load dependent dropdowns after setting form data
        try {
            // First load districts if not already loaded
            if (districts.length === 0) {
                const distRes = await apiService.getDistricts();
                const districtsList = distRes.data || distRes.record || [];
                setDistricts(districtsList);
            }

            // If district is set, load talukas
            if (mappedFormData.district_id) {
                console.log('Loading talukas for district:', mappedFormData.district_id);
                const talukaRes = await apiService.getTalukas(mappedFormData.district_id);
                const talukasList = talukaRes.data || talukaRes.record || [];
                setTalukas(talukasList);

                // If taluka is also set, load villages
                if (mappedFormData.taluka_id) {
                    console.log('Loading villages for district:', mappedFormData.district_id, 'taluka:', mappedFormData.taluka_id);
                    const villageRes = await apiService.getVillages(mappedFormData.district_id, mappedFormData.taluka_id);
                    const villagesList = villageRes.data || villageRes.record || [];
                    setVillages(villagesList);
                }
            }
        } catch (err) {
            console.error('Error loading dependent location data:', err);
        }
    };

    const handleDistrictClick = () => {
        setEditDistrict(true);
        // Pre-load talukas if district is already selected
        if (formData.district_id && talukas.length === 0) {
            apiService.getTalukas(formData.district_id)
                .then(response => {
                    const talukasList = response.data || response.record || [];
                    setTalukas(talukasList);
                })
                .catch(error => console.error('Error loading talukas:', error));
        }
    };

    const handleTalukaClick = () => {
        setEditTaluka(true);
        // Pre-load villages if both district and taluka are selected
        if (formData.district_id && formData.taluka_id && villages.length === 0) {
            apiService.getVillages(formData.district_id, formData.taluka_id)
                .then(response => {
                    const villagesList = response.data || response.record || [];
                    setVillages(villagesList);
                })
                .catch(error => console.error('Error loading villages:', error));
        }
    };

    const handleVillageClick = () => {
        setEditVillage(true);
    };

    const fetchLandholdingData = async (landIdToFetch) => {
        if (!landIdToFetch || landIdToFetch === 'undefined') {
            throw new Error('No valid land ID provided for fetching data.');
        }

        try {
            console.log('Fetching landholding data for ID:', landIdToFetch);
            const result = await apiService.getHighwayLandHoldingById(landIdToFetch);

            if (!result || (!result.ok && result.status !== 200)) {
                throw new Error(`HTTP error! status: ${result?.status || 'unknown'}`);
            }

            // Handle different response formats
            const data = result.data || result;

            if (!data) {
                throw new Error('No data received from API');
            }

            mapDataToFormState(data);
            console.log('Successfully loaded data from API');

        } catch (error) {
            console.error('Error fetching landholding data:', error);
            throw error;
        }
    };
    // ---------
    // Minimal helper — uses existing showAlert to inform user
    const checkAreaConstraints = (data) => {
        const total = parseFloat(data.total_area) || 0;
        const aqr = parseFloat(data.aqr_area) || 0;
        const irrig = parseFloat(data.irrigable_area) || 0;
        const nonIrrig = parseFloat(data.non_irrigable_area) || 0;

        let hasErrors = false;
        const newErrors = {};

        // Check if acquired area exceeds total area
        if (total > 0 && aqr > total) {
            newErrors.aqr_area = "Acquired area cannot exceed total area";
            hasErrors = true;
        }

        // Check if irrigable + non-irrigable exceeds total area
        if (aqr > 0 && (irrig + nonIrrig) > aqr) {
            newErrors.aqr_area = "Cultivable area and Non-Cultivable area cannot exceed acquired area";
            hasErrors = true;
        }

        // Update errors state
        setErrors(prev => {
            const updatedErrors = { ...prev };

            // Clear previous area-related errors
            delete updatedErrors.aqr_area;
            delete updatedErrors.total_area;
            delete updatedErrors.irrigable_area;

            // Add new errors if any
            return { ...updatedErrors, ...newErrors };
        });

        // Return validation status
        return !hasErrors;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newValue = type === "checkbox" ? (checked ? 1 : 0) : value;

        if (["irrigable_area", "non_irrigable_area", "total_area", "aqr_area"].includes(name)) {
            if (value === "") {
                newValue = "";
            } else if (/^\d*\.?\d{0,2}$/.test(value)) {      // allow typing up to 2 decimals
                // let the user keep typing, no rounding yet
                newValue = value;
            } else {
                // user typed more than 2 decimals → trim
                const parsed = parseFloat(value);
newValue = isNaN(parsed) ? "" : safeTwoDecimals(parsed).toString();            }
        }
        // --- Clamp logic: irrigable + non_irrigable must not exceed total ---
        if (name === "irrigable_area") {
            const maxAllowed = (formData.total_area || 0) - (formData.non_irrigable_area || 0);
            if (newValue > maxAllowed) {
                newValue = maxAllowed;
                setErrors((prev) => ({
                    ...prev,
                    irrigable_area: `Cultivable area cannot exceed ${maxAllowed} ${formData.unit_name}`,
                }));
            } else {
                setErrors((prev) => {
                    const { irrigable_area, ...rest } = prev;
                    return rest; // clear error
                });
            }
        }

        if (name === "non_irrigable_area") {
            const maxAllowed = (formData.total_area || 0) - (formData.irrigable_area || 0);
            if (newValue > maxAllowed) {
                newValue = maxAllowed;
                setErrors((prev) => ({
                    ...prev,
                    non_irrigable_area: `Non-cultivable area cannot exceed ${maxAllowed} ${formData.unit_name}`,
                }));
            } else {
                setErrors((prev) => {
                    const { non_irrigable_area, ...rest } = prev;
                    return rest; // clear error
                });
            }
        }  

        // FIXED: Handle area unit selection - now properly stores both ID and name
        if (name === "area_unit_id") {
            const selectedUnit = areaUnits.find((unit) => unit.area_unit_id == newValue);
            const simulated = {
                ...formData,
                area_unit_id: parseInt(newValue) || "",
                unit_name: selectedUnit ? selectedUnit.unit_name : "",
            };
            setFormData((prev) => ({
                ...prev,
                area_unit_id: parseInt(newValue) || "",
                unit_name: selectedUnit ? selectedUnit.unit_name : "",
            }));
            checkAreaConstraints(simulated);
            return;
        }

        // Handle district selection
        if (name === "district_id") {
            const selectedDistrict = districts.find((d) => d.dist_name === value);
            setSelectedDistrictId(selectedDistrict?.dist_no || null);

            const simulated = {
                ...formData,
                [name]: newValue,
                taluka_id: "",
                village_id: "",
            };
            setFormData((prev) => ({
                ...prev,
                [name]: newValue,
                taluka_id: "",
                village_id: "",
            }));

            setSelectedTalukaId(null);
            setEditDistrict(false);
            return;
        }

        // Handle taluka selection
        if (name === "taluka_id") {
            const selectedTaluka = talukas.find((t) => t.taluka_name === value);
            setSelectedTalukaId(selectedTaluka?.taluka_no || null);

            setFormData((prev) => ({
                ...prev,
                [name]: newValue,
                village_id: "",
            }));

            setEditTaluka(false);
            return;
        }

        // Handle village selection
        if (name === "village_id") {
            setEditVillage(false);
        }

        // Default state update
        setFormData((prev) => ({
            ...prev,
            [name]: newValue,
        }));

        // Run validation if relevant area field changed
        if (["irrigable_area", "non_irrigable_area", "total_area", "aqr_area"].includes(name)) {
            const simulated = {
                ...formData,
                [name]: newValue,
            };
            checkAreaConstraints(simulated);
        }
    };

// Handle new owner form data changes
const handleNewOwnerChange = (e) => {
  const { name, value } = e.target;
  let newValue = value;

  // Convert numeric fields
  if (['owner_total_area', 'owner_aqr_area'].includes(name)) {
    if (value === "") {
      newValue = "";
    } else if (/^\d*\.?\d{0,2}$/.test(value)) {
      newValue = value; // allow typing decimals
    } else {
      const parsed = parseFloat(value);
      newValue = isNaN(parsed) ? "" : safeTwoDecimals(parsed).toString();
    }
  }

  // Validate total area doesn't exceed remaining
  if (name === 'owner_total_area') {
    const enteredArea = parseFloat(value) || 0;
    if (enteredArea > remainingTotalArea) {
      showAlert(
        'Area Limit Exceeded',
        `Maximum remaining total area is ${remainingTotalArea} ${formData.unit_name}`,
        'warning'
      );
      newValue = remainingTotalArea;
    }
  }

  // Validate acquired area doesn't exceed remaining
  if (name === 'owner_aqr_area') {
    const enteredAcquiredArea = parseFloat(value) || 0;
    if (enteredAcquiredArea > remainingAcquiredArea) {
      showAlert(
        'Acquired Area Limit Exceeded',
        `Maximum remaining acquired area is ${remainingAcquiredArea} ${formData.unit_name}`,
        'warning'
      );
      newValue = remainingAcquiredArea;
    }
  }

  // ✅ Irrigable / Non-Irrigable validation
  if (['owner_irrigable_area', 'owner_non_irrigable_area'].includes(name)) {
    if (value === "") {
      newValue = "";
    } else {
      let parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        parsed = safeTwoDecimals(parsed); // keep 2 decimals
      } else {
        parsed = "";
      }

      // 🔹 Use acquired area, not total
      const acquired = parseFloat(newOwnerData.owner_aqr_area) || 0;

      const irrig = name === 'owner_irrigable_area'
        ? parsed
        : parseFloat(newOwnerData.owner_irrigable_area) || 0;

      const nonIrrig = name === 'owner_non_irrigable_area'
        ? parsed
        : parseFloat(newOwnerData.owner_non_irrigable_area) || 0;

      if (irrig + nonIrrig > acquired) {
        const maxAllowed = acquired - (
          name === 'owner_irrigable_area'
            ? (parseFloat(newOwnerData.owner_non_irrigable_area) || 0)
            : (parseFloat(newOwnerData.owner_irrigable_area) || 0)
        );

        parsed = Math.max(0, safeTwoDecimals(maxAllowed));

        showAlert(
          'Validation',
          `${translations[language][name === 'owner_irrigable_area'
            ? 'IrrigableArea'
            : 'NonIrrigableArea']} cannot exceed ${maxAllowed.toFixed(4)} ${formData.unit_name}`,
          'warning'
        );
      }

      newValue = parsed;
    }
  }

  setNewOwnerData(prev => ({
    ...prev,
    [name]: newValue
  }));
};


    // Add new owner/occupant
    const handleAddOwner = async (e) => {
        e.preventDefault();

        if (!currentLandId || !formData.project_id || !formData.proposal_id) {
            showAlert('Error', 'Missing required data to add owner', 'error');
            return;
        }

        // Validate total area
        const enteredTotalArea = parseFloat(newOwnerData.owner_total_area) || 0;
        if (enteredTotalArea > remainingTotalArea) {
            showAlert(
                'Invalid Area',
                `Total area cannot exceed remaining area of ${remainingTotalArea} ${formData.unit_name}`,
                'error'
            );
            return;
        }

        if (enteredTotalArea <= 0) {
            showAlert('Invalid Area', 'Total area must be greater than 0', 'error');
            return;
        }

        // NEW: Validate acquired area
        const enteredAcquiredArea = parseFloat(newOwnerData.owner_aqr_area) || 0;
        if (enteredAcquiredArea > remainingAcquiredArea) {
            showAlert(
                'Invalid Acquired Area',
                `Acquired area cannot exceed remaining acquired area of ${remainingAcquiredArea} ${formData.unit_name}`,
                'error'
            );
            return;
        }

        // Validate that acquired area doesn't exceed owner's total area
        if (enteredAcquiredArea > enteredTotalArea) {
            showAlert('Invalid Area', 'Acquired area cannot exceed owner\'s total area', 'error');
            return;
        }

        // for irrigable and non-irrigable area
// ✅ Validate irrigable + non-irrigable
  const irrig = parseFloat(newOwnerData.owner_irrigable_area) || 0;
  const nonIrrig = parseFloat(newOwnerData.owner_non_irrigable_area) || 0;

  if (irrig + nonIrrig > enteredTotalArea) {
    showAlert(
      'Invalid Area',
      `Irrigable + Non-Irrigable area cannot exceed ${enteredTotalArea} ${formData.unit_name}`,
      'error'
    );
    return;
  }
        try {
            setAddingOwner(true);

            const payload = {
                project_id: formData.project_id,
                proposal_id: formData.proposal_id,
                LandAcquisition_id: currentLandId,
                owner_name: newOwnerData.owner_name,
                survey_no: formData.survey_no,
                dist_name: formData.district_id,
                taluka_name: formData.taluka_id,
                village_name: formData.village_id,
                owner_total_area: parseFloat(newOwnerData.owner_total_area),
                owner_aqr_area: parseFloat(newOwnerData.owner_aqr_area),
                owner_irrigable_area: parseFloat(newOwnerData.owner_irrigable_area) || 0,
                owner_non_irrigable_area: parseFloat(newOwnerData.owner_non_irrigable_area) || 0,
                is_active: 1,
            };

            console.log('Adding new owner with payload:', payload);

            const result = await apiService.addHighwayMeasurement(payload);

            if (result && (result.ok || result.status === 200)) {
                showAlert('Success', 'Owner added successfully!', 'success');

                // Reset form
                resetNewOwnerForm();
                setShowAddOwnerForm(false);

                // Refresh the landholding data to get updated owners list
                setTimeout(() => {
                    fetchLandholdingData(currentLandId);
                }, 1000);

                // Call onSuccess callback to refresh parent data and hide form
                setTimeout(() => {
                    if (typeof onSuccess === "function") {
                        onSuccess();
                    } else if (typeof onBack === 'function') {
                        // Fallback to onBack if onSuccess is not provided
                        onBack();
                    }
                }, 2000);

            } else {
                throw new Error('Failed to add owner');
            }

        } catch (error) {
            console.error('Error adding owner:', error);
            showAlert('Error', `Failed to add owner: ${error.message}`, 'error');
        } finally {
            setAddingOwner(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Clear previous errors first
        setErrors({});

        // Validate that we have a valid landId
        if (!currentLandId || currentLandId === 'undefined' || currentLandId === undefined) {
            setError('No valid land ID available for updating. Cannot proceed with save.');
            return;
        }

        // Validate area constraints BEFORE proceeding
        const isValid = checkAreaConstraints(formData);
        if (!isValid) {
            // Show alert to user about validation errors
            showAlert(
                'Validation Error',
                'Please fix the area validation errors before saving.',
                'error'
            );
            return; // Stop submission
        }

        try {
            setSaving(true);
            setError(null);

            // Prepare data for API - Exact payload structure as requested
            const submitData = {
                project_id: formData.project_id,
                proposal_id: formData.proposal_id,
                dist_name: formData.district_id,
                taluka_name: formData.taluka_id,
                village_name: formData.village_id,
                aqr_area: formData.aqr_area,
                irrigable_area: formData.irrigable_area.toString(),
                irrigable_type: formData.irrigable_type,
                is_active: formData.is_active === true || formData.is_active === 1 ? 1 : 0,
                landholder_name: formData.landholder_name,
                non_irrigable_area: formData.non_irrigable_area.toString(),
                survey_no: formData.survey_no,
                survey_type: formData.survey_type,
                total_area: formData.total_area,
                north: formData.north,
                east: formData.east,
                west: formData.west,
                south: formData.south,
                area_unit_id: formData.area_unit_id,
                land_holding_type: formData.land_holding_type, // now sends "Class-I" or "Class-II"

            };

            console.log('Updating landholding with ID:', currentLandId);
            console.log('Submit data:', submitData);

            // Make the API call
            const result = await apiService.updateHighwayLandHolding(currentLandId, submitData);

            // Handle the response
            if (result && (result.ok || result.status === 200)) {
                console.log('Update successful:', result.data || result);

                // Clear localStorage after successful update
                localStorage.removeItem('editingLandholding');

                // Show success message
                showAlert('Success', 'Landholding updated successfully!', 'success');

                if (typeof onSuccess === "function") {
                    onSuccess(); // This calls handleEditSuccess and refreshes parent table
                }

                setTimeout(() => {
                    fetchLandholdingData(currentLandId);
                }, 1000);

            } else {
                throw new Error('Update failed - invalid response from server');
            }

        } catch (error) {
            console.error('Error updating landholding:', error);
            setError(`Failed to update landholding: ${error.message}`);

            // Also show alert for API errors
            showAlert(
                'Update Failed',
                `Failed to update landholding: ${error.message}`,
                'error'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleRetryLoad = () => {
        if (currentLandId) {
            fetchLandholdingData(currentLandId)
                .then(() => {
                    setError(null);
                })
                .catch((error) => {
                    setError(`Failed to reload data: ${error.message}`);
                });
        } else {
            initializeComponent();
        }
    };

    // Handle showing add owner form with pre-filled data
    const handleShowAddOwnerForm = () => {
        // Close edit form if it's open (NEW CODE)
        if (showEditOwnerForm) {
            setShowEditOwnerForm(false);
            resetEditOwnerForm();
        }

        // Existing code for opening add form
        resetNewOwnerForm();
        setShowAddOwnerForm(true);

        // Scroll to the add form once it's rendered
        setTimeout(() => {
            if (addOwnerFormRef.current) {
                addOwnerFormRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }
        });
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg border border-gray-300 shadow-lg w-full max-w-7xl mx-auto my-4 sm:my-6">
                <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                    <div className="text-gray-600">{translations[language].Loadinglandholdingdata}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg border border-gray-300 shadow-lg w-full max-w-7xl mx-auto my-4 sm:my-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-900 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-t-lg">
                <div className="flex items-center justify-between">
                    <h2 className="text-base sm:text-lg font-semibold flex items-center">
                        <Save className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                        <span className="hidden sm:inline">{translations[language].EditLandholding}</span>
                        <span className="sm:hidden">{translations[language].Edit}</span>
                    </h2>
                    <button
                        onClick={onBack}
                        className="text-white hover:text-gray-200 transition-colors duration-200"
                        title="Close Form"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Form Content */}
            <div className="p-4 sm:p-6">
                {error && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md">
                        <div className="flex items-center justify-between">
                            <span className="text-sm">{error}</span>
                            <button
                                onClick={handleRetryLoad}
                                className="ml-2 text-sm underline hover:no-underline font-medium"
                            >
                                {translations[language].Retry}
                            </button>
                        </div>
                    </div>
                )}

                {!currentLandId && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-md">
                        <div className="flex items-center justify-between">
                            <span className="text-sm">{translations[language].WarningNolandIDavailableUpdatesmaynotworkproperly}</span>
                            <button
                                onClick={initializeComponent}
                                className="ml-2 text-sm underline hover:no-underline font-medium"
                            >
                                {translations[language].Reload}
                            </button>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 sm:space-y-6">
                        {/* Location Fields */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                            {/* DISTRICT - dropdown */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language]?.district}<span className="text-red-500 ml-1">*</span>
                                </label>
                                <select
                                    name="district_id"
                                    value={formData.district_id}
                                    onChange={handleChange}
                                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    required
                                >
                                    <option value="" disabled>{translations[language]?.SelectDistrict}</option>
                                    {districts.map((district) => (
                                        <option key={district.dist_name || district.id} value={district.dist_name}>
                                            {district.dist_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* TALUKA - dropdown */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language]?.taluka}<span className="text-red-500 ml-1">*</span>
                                </label>
                                <select
                                    name="taluka_id"
                                    value={formData.taluka_id}
                                    onChange={handleChange}
                                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    required
                                    disabled={!formData.district_id}
                                >
                                    <option value="" disabled>{translations[language]?.SelectTaluka}</option>
                                    {talukas.map((taluka) => (
                                        <option key={taluka.taluka_name || taluka.id} value={taluka.taluka_name}>
                                            {taluka.taluka_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* VILLAGE - dropdown */}
                            <div className="space-y-1 sm:space-y-2 sm:col-span-2 lg:col-span-1">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language]?.village}<span className="text-red-500 ml-1">*</span>
                                </label>
                                <select
                                    name="village_id"
                                    value={formData.village_id}
                                    onChange={handleChange}
                                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    required
                                    disabled={!formData.district_id || !formData.taluka_id}
                                >
                                    <option value="" disabled>{translations[language]?.SelectVillage}</option>
                                    {villages.map((village) => (
                                        <option key={village.village_name || village.id} value={village.village_name}>
                                            {village.village_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Survey Type, Survey No, Area Fields */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">

                            {/* Survey Type */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language].SurveyType}<span className="text-red-500 ml-1">*</span>
                                </label>
                                <select
                                    name="survey_type"
                                    value={formData.survey_type}
                                    onChange={handleChange}
                                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    required
                                >
                                    <option value="" disabled>{translations[language].SelectSurveyType}</option>
                                    <option value="सर्व्हे क्रमांक">{translations[language].SurveyNo}</option>
                                    <option value="गट क्रमांक">{translations[language].GatNo}</option>
                                    <option value="प्लॉट क्रमांक">{translations[language].PlotNo}</option>
                                    <option value="घर क्रमांक">{translations[language].GharNo}</option>
                                </select>
                            </div>

                            {/* Survey No */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language].SurveyNo}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="survey_no"
                                    value={formData.survey_no}
                                    onChange={handleChange}
                                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    placeholder={translations[language].EnterSurveyNo}
                                    disabled={!formData.survey_type}
                                    required
                                    readOnly
                                />
                            </div>

                            {/* Area Unit */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language].AreaUnit}<span className="text-red-500 ml-1">*</span>
                                </label>
                                <select
                                    name="area_unit_id"  // Changed from "unit_name" to "area_unit_id"
                                    value={formData.area_unit_id || ''}  // Use area_unit_id as value
                                    onChange={handleChange}
                                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    required
                                    disabled={loading}
                                >
                                    <option value="" disabled>{translations[language].SelectAreaUnit}</option>
                                    {areaUnits.map((unit) => (
                                        <option
                                            key={unit.area_unit_id || unit.value || unit.unit_code}
                                            value={unit.area_unit_id}  // Use area_unit_id as value
                                        >
                                            {unit.unit_name}  {/* Display unit_name to user */}
                                        </option>
                                    ))}
                                </select>
                                {loading && <span className="text-xs text-gray-500">{translations[language].loadingAreaUnits}</span>}
                            </div>


                            {/* Total Area */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language]?.totalArea || 'Total Area'} ({formData.unit_name})
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="total_area"
                                    value={formData.total_area}
                                    onChange={handleChange}
                                    className={`w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${errors.total_area ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    // step="0.01"
                                    // min="0"
                                    required
                                    placeholder={translations[language].EnterTotalArea}
                                />
                                {/* Show error for total area */}
                                {errors.total_area && (
                                    <span className="text-xs text-red-600 mt-1 block">
                                        {errors.total_area}
                                    </span>
                                )}
                            </div>

                            {/* Acquired Area */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language]?.acquiredArea} ({formData.unit_name})
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="aqr_area"
                                    value={formData.aqr_area}
                                    onChange={handleChange}
                                    className={`w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${errors.aqr_area ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    // step="0.01"
                                    // min="0"
                                    required
                                    placeholder={translations[language].EnterAcquiredArea}
                                />
                                {/* Show error for acquired area */}
                                {errors.aqr_area && (
                                    <span className="text-xs text-red-600 mt-1 block">
                                        {errors.aqr_area}
                                        {formData.total_area && (
                                            <> <br />{translations[language].Maximum}: {formData.total_area} {formData.unit_name}</>
                                        )}
                                    </span>
                                )}
                                {!formData.area_unit_id && (
                                    <span className="text-xs text-amber-600">{translations[language].PleaseSelectAreaUnitFirst}</span>
                                )}
                            </div>
                        </div>

                        {/* Landholder Name, Irrigable Areas */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                            {/* Landholder Name */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language]?.landholderName || "Landholder's Name"}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="landholder_name"
                                    value={formData.landholder_name}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    placeholder={translations[language].EnterName}
                                />
                            </div>
                            {/* Irrigable/ Cultivable */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language]?.irrigableArea || 'Cultivable Area'} ({formData.unit_name})
                                </label>
                                <input
                                    type="text"
                                    name="irrigable_area"
                                    value={formData.irrigable_area}
                                    onChange={handleChange}
                                    placeholder={translations[language].EnterIrrigableeArea}
                                    className={`w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border rounded-md 
                                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white 
                                    ${errors.irrigable_area ? "border-red-500" : "border-gray-300"}`}
                                />
                                {errors.irrigable_area && (
                                    <span className="text-xs text-red-600 mt-1 block">
                                        {errors.irrigable_area}
                                    </span>
                                )}
                            </div>
                            {/* Non-irrigable/ non-cultivable */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language]?.nonIrrigableArea || 'Non-Cultivable Area'} ({formData.unit_name})
                                </label>
                                <input
                                    type="text"
                                    name="non_irrigable_area"
                                    value={formData.non_irrigable_area}
                                    onChange={handleChange}
                                    className={`w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border rounded-md 
                                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white 
                                    ${errors.non_irrigable_area ? "border-red-500" : "border-gray-300"}`}
                                    placeholder={translations[language].EnterNonIrrigableArea}
                                />
                                {errors.non_irrigable_area && (
                                    <span className="text-xs text-red-600 mt-1 block">
                                        {errors.non_irrigable_area}
                                    </span>
                                )}
                            </div>
                            {/* भूधारण पद्धती/ Class type */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                    Select Landholding Type
                                </label>
                                <select
                                    name="land_holding_type"
                                    value={formData.land_holding_type}
                                    onChange={handleChange}
                                    className={`w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${errors.landholder_name ? "border-red-400 bg-red-50" : "border-gray-300"
                                        }`}
                                >
                                    <option value="" disabled>Select Landholding Type</option>
                                    {landHoldingTypes.map((type) => (
                                        <option key={type.land_holding_type_id} value={type.land_holding_type}>
                                            {type.land_holding_type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* N-E-W-S  4 Columns */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                            {/* North */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language].North}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="north"
                                    value={formData.north}
                                    onChange={handleChange}
                                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    placeholder={translations[language].EnterNorth}
                                    required
                                />
                            </div>

                            {/* East */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language].East}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="east"
                                    value={formData.east}
                                    onChange={handleChange}
                                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    placeholder={translations[language].EnterEast}
                                    required
                                />
                            </div>

                            {/* West */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language].West}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="west"
                                    value={formData.west}
                                    onChange={handleChange}
                                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    placeholder={translations[language].EnterWest}
                                    required
                                />
                            </div>

                            {/* South */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language].South}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="south"
                                    value={formData.south}
                                    onChange={handleChange}
                                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    placeholder={translations[language].EnterSouth}
                                    required
                                />
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={onBack}
                                className="w-full sm:w-auto px-4 sm:px-6 py-2 text-xs sm:text-sm bg-gray-100 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-200 hover:border-gray-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={saving}
                            >
                                {translations[language]?.cancel || 'Cancel'}
                            </button>
                            <button
                                type="submit"
                                className="w-full sm:w-auto px-6 sm:px-8 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                                disabled={saving || !currentLandId}
                                title={!currentLandId ? 'No valid land ID - cannot save' : ''}
                            >
                                <Save className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                                {saving ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span className="hidden sm:inline">{translations[language].Saving}</span>
                                        <span className="sm:hidden">{translations[language].Save}</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="hidden sm:inline">{translations[language]?.saveChanges || 'Save Changes'}</span>
                                        <span className="sm:hidden">{translations[language].Save}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>

                {/* Owners/Occupants Section */}
                <div className="mt-8 border-t pt-6">
                    <div className="flex items-center justify-between mb-4">

                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <User className="h-5 w-5 mr-2" />
                            {translations[language].LandOccupants}
                        </h3>
                        <button
                            onClick={(e) => {
                                if (isTotalAreaComplete || isAcquiredAreaComplete) {
                                    showAlert(
                                        "All areas have been distributed",
                                        "Total area and Acquired area are already completed. Please update Total or Acquired area!",
                                        "warning"
                                    );
                                    // ensure form is visible
                                    handleShowAddOwnerForm();

                                    return;
                                }
                                handleShowAddOwnerForm();
                            }}
                            className={`px-4 py-2 rounded-md transition-colors font-medium text-sm flex items-center bg-green-600 text-white hover:bg-green-700`}
                            disabled={!currentLandId}
                            title={
                                isTotalAreaComplete && isAcquiredAreaComplete
                                    ? 'All areas have been distributed'
                                    : !currentLandId
                                        ? 'No land ID available'
                                        : 'Add new occupant'
                            }
                        >
                            <Plus className="h-4 w-4 mr-1" />
                            {translations[language].AddOccupant}
                        </button>
                    </div>


                    {/* Existing Owners Display */}
                    {existingOwners.length > 0 ? (
                        <div className="space-y-4 mb-8">
                            <h4 className="text-base font-semibold text-gray-800">{translations[language].ExistingOwners}</h4>

                            <div className="overflow-x-auto shadow-md rounded-lg border border-gray-200 bg-white">
                                <table className="min-w-full table-auto text-sm text-left text-gray-700">
                                    <thead className="bg-blue-100 text-gray-600 font-medium">
                                        <tr>
                                            <th className="px-4 py-3">{translations[language].Number1}</th>
                                            <th className="px-4 py-3">{translations[language].OwnerName}</th>
                                            <th className="px-4 py-3">{translations[language].SurveyNo}</th>
                                            <th className="px-4 py-3">{translations[language].TotalArea} ({formData.unit_name})</th>
                                            <th className="px-4 py-3">{translations[language].AcquiredArea} ({formData.unit_name})</th>
                                            <th className="px-4 py-3">{translations[language].Action}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {existingOwners.map((owner, index) => (
                                            <tr key={owner.land_owner_id || index} className="border-b hover:bg-gray-50 transition">
                                                <td className="px-4 py-3">{index + 1}</td>
                                                <td className="px-4 py-3 font-semibold text-gray-800">{owner.owner_name}</td>
                                                <td className="px-4 py-3">{owner.survey_no}</td>
                                                <td className="px-4 py-3">{owner.owner_total_area}</td>
                                                <td className="px-4 py-3">{owner.owner_aqr_area}</td>
                                                <td className="px-4 py-3">
                                                    <button
                                                        onClick={() => handleShowEditOwnerForm(owner)}
                                                        className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs font-medium"
                                                        title="Edit Owner"
                                                    >
                                                        {translations[language].Edit}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    ) : (
                        <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg mb-6">
                            <User className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                            <p>{translations[language].NoOwnersFound}</p>
                        </div>
                    )}

                    {/* Edit Owner Form */}
                    {showEditOwnerForm && (
                        <div ref={editOwnerFormRef} className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-semibold text-blue-900">{translations[language].EditOccupant}</h4>
                                <button
                                    onClick={() => {
                                        setShowEditOwnerForm(false);
                                        resetEditOwnerForm();
                                    }}
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleEditOwner}>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                        {/* Owner Name */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {translations[language].OwnerName}<span className="text-red-500 ml-1">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="owner_name"
                                                value={editOwnerData.owner_name}
                                                onChange={handleEditOwnerChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                placeholder="Enter owner name"
                                                required
                                            />
                                        </div>

                                        {/* Survey No - Auto-filled and read-only */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {translations[language].SurveyNo}<span className="text-red-500 ml-1">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="survey_no"
                                                value={editOwnerData.survey_no}
                                                onChange={handleEditOwnerChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                                                placeholder="Auto-filled from landholding"
                                                readOnly
                                            />
                                        </div>

                                        {/* Total Area */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {translations[language].TotalArea} ({formData.unit_name})<span className="text-red-500 ml-1">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="owner_total_area"
                                                value={editOwnerData.owner_total_area}
                                                onChange={handleEditOwnerChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                placeholder={translations[language].EnterTotalArea}
                                                // step="0.01"
                                                // min="0"
                                                max={calculateEditingAreaLimits(originalOwnerData).availableTotalArea}
                                                required
                                            />
                                            <span className="text-xs text-blue-600 block">
                                                {translations[language].MaxAvailable}: {calculateEditingAreaLimits(originalOwnerData).availableTotalArea.toFixed(4)} {formData.unit_name}
                                            </span>
                                        </div>

                                        {/* Acquired Area */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {translations[language].AcquiredArea} ({formData.unit_name})<span className="text-red-500 ml-1">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="owner_aqr_area"
                                                value={editOwnerData.owner_aqr_area}
                                                onChange={handleEditOwnerChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                placeholder={translations[language].EnterAcquiredArea}
                                                // step="0.01"
                                                // min="0"
                                                max={Math.min(
                                                    calculateEditingAreaLimits(originalOwnerData).availableAcquiredArea,
                                                    editOwnerData.owner_total_area || calculateEditingAreaLimits(originalOwnerData).availableAcquiredArea
                                                )}
                                                required
                                            />
                                            <span className="text-xs text-green-600 block">
                                                {translations[language].MaxAvailable}: {Math.min(
                                                    calculateEditingAreaLimits(originalOwnerData).availableAcquiredArea,
                                                    editOwnerData.owner_total_area || calculateEditingAreaLimits(originalOwnerData).availableAcquiredArea
                                                ).toFixed(4)} {formData.unit_name}
                                                <br />
                                                <span className="text-gray-500">
                                                    ({translations[language].LimitedByAvailableAcquired}: {calculateEditingAreaLimits(originalOwnerData).availableAcquiredArea.toFixed(4)} & {translations[language].OwnersTotal}: {editOwnerData.owner_total_area || 0})
                                                </span>
                                            </span>
                                        </div>

                                        {/*Owner Irrigable Area */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {translations[language].IrrigableArea} ({formData.unit_name})
                                            </label>
                                            <input
                                                type="text"
                                                name="owner_irrigable_area"
                                                value={editOwnerData.owner_irrigable_area}
                                                onChange={handleEditOwnerChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md 
                                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                placeholder={translations[language].EnterIrrigableeArea}
                                            // step="0.01"
                                            // min="0"
                                            />
                                        </div>

                                        {/* Owner Non-Irrigable Area */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {translations[language].NonIrrigableArea} ({formData.unit_name})
                                            </label>
                                            <input
                                                type="text"
                                                name="owner_non_irrigable_area"
                                                value={editOwnerData.owner_non_irrigable_area}
                                                onChange={handleEditOwnerChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md 
                                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                placeholder={translations[language].EnterNonIrrigableArea}
                                            />
                                        </div>

                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowEditOwnerForm(false);
                                            resetEditOwnerForm();
                                        }}
                                        className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-200 transition-colors font-medium text-sm"
                                        disabled={editingOwner}
                                    >
                                        {translations[language].cancel}
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={editingOwner}
                                    >
                                        {editingOwner ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {translations[language].Updating}
                                            </>
                                        ) : (
                                            <>
                                                <Save className="h-4 w-4 mr-1" />
                                                {translations[language].UpdateOccupant}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Add New Owner Form */}
                    {showAddOwnerForm && (
                        <div ref={addOwnerFormRef} className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-semibold text-blue-900">{translations[language].AddNewOccupantBtn}</h4>
                                <button
                                    onClick={() => {
                                        setShowAddOwnerForm(false);
                                        resetNewOwnerForm();
                                    }}
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleAddOwner}>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                        {/* Owner Name */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {translations[language].OwnerName}<span className="text-red-500 ml-1">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="owner_name"
                                                value={newOwnerData.owner_name}
                                                onChange={handleNewOwnerChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                placeholder="Enter owner name"
                                                required
                                            />
                                        </div>

                                        {/* Survey No - Auto-filled and read-only */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {translations[language].SurveyNo}<span className="text-red-500 ml-1">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="survey_no"
                                                value={newOwnerData.survey_no}
                                                onChange={handleNewOwnerChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                                                placeholder="Auto-filled from landholding"
                                                readOnly
                                            />
                                        </div>

                                        {/* Total Area */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {translations[language].TotalArea} ({formData.unit_name})<span className="text-red-500 ml-1">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="owner_total_area"
                                                value={newOwnerData.owner_total_area}
                                                onChange={handleNewOwnerChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                placeholder={translations[language].EnterTotalArea}
                                                // step="0.01"
                                                // min="0"
                                                max={remainingTotalArea}
                                                required
                                            />
                                            <span className="text-xs text-blue-600 block">Max: {remainingTotalArea} {formData.unit_name}</span>
                                        </div>

                                        {/* NEW: Acquired Area with validation */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {translations[language].AcquiredArea} ({formData.unit_name})<span className="text-red-500 ml-1">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="owner_aqr_area"
                                                value={newOwnerData.owner_aqr_area}
                                                onChange={handleNewOwnerChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                placeholder={translations[language].EnterAcquiredArea}
                                                // step="0.01"
                                                // min="0"
                                                max={Math.min(remainingAcquiredArea, newOwnerData.owner_total_area || remainingAcquiredArea)}
                                                required
                                            />
                                            <span className="text-xs text-green-600 block">
                                                {translations[language].Max}: {Math.min(remainingAcquiredArea, newOwnerData.owner_total_area || remainingAcquiredArea).toFixed(4)} {formData.unit_name}
                                                <br />
                                                <span className="text-gray-500">
                                                    ({translations[language].LimitedByRemainingAcquired}: {remainingAcquiredArea} & {translations[language].OwnersTotal}: {newOwnerData.owner_total_area || 0})
                                                </span>
                                            </span>
                                        </div>

                                        {/* Owner Irrigable Area */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {translations[language].IrrigableArea} ({formData.unit_name})
                                            </label>
                                            <input
                                                type="text"
                                                name="owner_irrigable_area"
                                                value={newOwnerData.owner_irrigable_area}
                                                onChange={handleNewOwnerChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md 
                                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                placeholder={translations[language].EnterIrrigableeArea}
                                            />
                                        </div>

                                        {/* Owner Non-Irrigable Area */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {translations[language].NonIrrigableArea} ({formData.unit_name})
                                            </label>
                                            <input
                                                type="text"
                                                name="owner_non_irrigable_area"
                                                value={newOwnerData.owner_non_irrigable_area}
                                                onChange={handleNewOwnerChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md 
                                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                placeholder={translations[language].EnterNonIrrigableArea}
                                        
                                            />
                                        </div>

                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowAddOwnerForm(false);
                                            resetNewOwnerForm();
                                        }}
                                        className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-200 transition-colors font-medium text-sm"
                                        disabled={addingOwner}
                                    >
                                        {translations[language].cancel}
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={addingOwner}
                                    >
                                        {addingOwner ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {translations[language].Adding}
                                            </>
                                        ) : (
                                            <>
                                                <Plus className="h-4 w-4 mr-1" />
                                                {translations[language].AddNewOccupantBtn}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* UPDATED: Dual Area Summary Card - Shows both total and acquired area tracking */}
                    <div className="mb-6">
                        <div className="flex flex-col lg:flex-row gap-6">
                            {/* Total Area Summary */}
                            <div className="flex-1 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                                <h3 className="text-lg text-center font-semibold text-blue-900 mb-3">{translations[language].TotalAreaDistributionSummary}</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-800">{formData.total_area}</div>
                                        <div className="text-sm text-blue-600">{translations[language].LandTotalArea} ({formData.unit_name})</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-800">{totalCompletedOwnerArea.toFixed(4)}</div>
                                        <div className="text-sm text-green-600">{translations[language].DistributedToOwners} ({formData.unit_name})</div>
                                    </div>
                                    <div className="text-center">
                                        <div className={`text-2xl font-bold ${remainingTotalArea > 0 ? 'text-orange-800' : 'text-green-800'}`}>
                                            {remainingTotalArea.toFixed(4)}
                                        </div>
                                        <div className={`text-sm ${remainingTotalArea > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                            {translations[language].Remaining} ({formData.unit_name})
                                        </div>
                                    </div>
                                </div>
                                
                                {isTotalAreaComplete && (
                                    <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded-md text-center">
                                        <span className="text-green-800 font-medium">
                                            ✓ {translations[language].AllAreaDistributed}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Acquired Area Summary */}
                            <div className="flex-1 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                                <h3 className="text-lg text-center font-semibold text-green-900 mb-3">{translations[language].AcquiredAreaDistributionSummary}</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-800">{formData.aqr_area}</div>
                                        <div className="text-sm text-green-600">{translations[language].LandAcquiredArea} ({formData.unit_name})</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-800">{totalCompletedAcquiredArea.toFixed(4)}</div>
                                        <div className="text-sm text-blue-600">{translations[language].DistributedToOwners} ({formData.unit_name})</div>
                                    </div>
                                    <div className="text-center">
                                        <div className={`text-2xl font-bold ${remainingAcquiredArea > 0 ? 'text-orange-800' : 'text-green-800'}`}>
                                            {remainingAcquiredArea.toFixed(4)}
                                        </div>
                                        <div className={`text-sm ${remainingAcquiredArea > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                            {translations[language].Remaining} ({formData.unit_name})
                                        </div>
                                    </div>
                                </div>
                                {isAcquiredAreaComplete && (
                                    <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded-md text-center">
                                        <span className="text-green-800 font-medium">
                                            ✓ {translations[language].AllAreaDistributed}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>

            </div>

            {/* Simple Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-sm mx-4">
                        <div className="p-6">
                            <div className={`w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center ${confirmConfig.type === 'danger' ? 'bg-red-100' :
                                confirmConfig.type === 'warning' ? 'bg-yellow-100' :
                                    'bg-blue-100'
                                }`}>
                                {confirmConfig.type === 'danger' ? '⚠' :
                                    confirmConfig.type === 'warning' ? '⚠' : '?'}
                            </div>

                            <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                                {confirmConfig.title}
                            </h3>

                            <p className="text-gray-600 text-sm mb-6 text-center">
                                {confirmConfig.message}
                            </p>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        if (confirmConfig.onCancel) confirmConfig.onCancel();
                                    }}
                                    className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                                >
                                    {translations[language].Cancel}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        if (confirmConfig.onConfirm) confirmConfig.onConfirm();
                                    }}
                                    className={`flex-1 py-2 px-4 text-white rounded-md transition-colors ${confirmConfig.type === 'danger' ? 'bg-red-600 hover:bg-red-700' :
                                        confirmConfig.type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' :
                                            'bg-blue-600 hover:bg-blue-700'
                                        }`}
                                >
                                    {translations[language].Confirm}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showAlertModal && (
                <div className="fixed top-4 right-4 z-50">
                    <div className={`bg-white rounded-lg shadow-lg border-l-4 p-4 max-w-sm ${alertConfig.type === 'error' ? 'border-red-500' :
                        alertConfig.type === 'warning' ? 'border-yellow-500' :
                            alertConfig.type === 'info' ? 'border-blue-500' :
                                'border-green-500'
                        }`}>
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${alertConfig.type === 'error' ? 'bg-red-100' :
                                    alertConfig.type === 'warning' ? 'bg-yellow-100' :
                                        alertConfig.type === 'info' ? 'bg-blue-100' :
                                            'bg-green-100'
                                    }`}>
                                    <span className={`font-bold ${alertConfig.type === 'error' ? 'text-red-600' :
                                        alertConfig.type === 'warning' ? 'text-yellow-600' :
                                            alertConfig.type === 'info' ? 'text-blue-600' :
                                                'text-green-600'
                                        }`}>
                                        {alertConfig.type === 'error' ? '✕' :
                                            alertConfig.type === 'warning' ? '⚠' :
                                                alertConfig.type === 'info' ? 'ℹ' : '✓'}
                                    </span>
                                </div>
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-sm font-medium text-gray-900">{alertConfig.title}</h3>
                                <p className="text-sm text-gray-600 mt-1">{alertConfig.message}</p>
                            </div>
                            <button
                                onClick={() => setShowAlertModal(false)}
                                className="ml-4 text-gray-400 hover:text-gray-600"
                            >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HighwayEditLandholdingList;